import { withNamespaces } from "react-i18next";
import { withRouter, useHistory } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  ModalFooter,
  Label,
  FormGroup,
  Input,
} from "reactstrap";
import Select from "react-select";
import Spinner from "react-bootstrap/Spinner";
import DataProtectionTomsService from "src/modules/data-protection/apis/DataProtectionTomsService";
import { connect } from "react-redux";
import { getTomId } from "../utils/tomIdentifiers";

const CloneModal = ({
  t,
  showModal,
  hideModal,
  onComplete,
  selectedTom,
  Organization,
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
  const [copySubcontractors, setCopySubcontractors] = useState(true);

  const {
    isFetching: legalEntitiesFetching,
    data: legalEntities,
    isError: legalEntitiesError,
  } = useQuery({
    queryKey: ["gdpr-fetch-organization-legal-entities-for-clone", Organization?.id],
    queryFn: async () => {
      const service = DataProtectionTomsService.getInstance();
      return await service.legalEntitiesWithouTOM(Organization?.id);
    },
    enabled: showModal,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching legal entities."), {
        type: "error",
      });
    },
  });

  const legalEntityOptions = useMemo(() => {
    return (legalEntities || []).map((item) => ({
      label: item.title,
      value: item.id,
    }));
  }, [legalEntities]);

  useEffect(() => {
    if (!showModal) {
      setName("");
      setSelectedLegalEntities([]);
      setCopySubcontractors(true);
      return;
    }

    setName(selectedTom?.name || "");
  }, [showModal, selectedTom]);

  const { mutate: cloneTOMMutation, isLoading: isCloningTom } = useMutation({
    mutationFn: async ({ tomId, payload }) => {
      const service = DataProtectionTomsService.getInstance();
      return await service.cloneTOM(payload, tomId);
    },
    onSuccess: (response) => {
      toast(t("New TOM created successfully."), {
        type: "success",
      });

      onComplete && onComplete();
      hideModal();

      const tomId = getTomId(response);
      if (tomId) {
        history.push(`/admin/data-protection/toms/${tomId}/details`);
      } else{
        queryClient.invalidateQueries(["data-protection-toms-list"]);
      }
    },
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while cloning the TOM."), {
        type: "error",
      });
    },
  });

  const handleSubmit = () => {
    const tomId = getTomId(selectedTom);

    if (!tomId) {
      toast(t("No TOM selected."), {
        type: "error",
      });
      return;
    }

    if (!selectedLegalEntities?.length) {
      toast(t("Please select at least one legal entity."), {
        type: "error",
      });
      return;
    }

    const payload = {
      legal_entity_ids: selectedLegalEntities.map((item) => Number(item.value)),
      copy_subcontractors: copySubcontractors,
    };

    if (name.trim()) {
      payload.name = name.trim();
    }

    cloneTOMMutation({
      tomId,
      payload,
    });
  };

  const actionInProgress = legalEntitiesFetching || isCloningTom;

  return (
    <Modal isOpen={showModal} backdrop="static" keyboard={false} size="md">
      <ModalHeader toggle={hideModal}>{t("Clone TOM")}</ModalHeader>

      <ModalBody>
        <Row>
          <Col sm="12" className="mb-3">
            <FormGroup>
              <Label>{t("Name / Label")}</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col sm="12" className="mb-3">
            <FormGroup>
              <Label>{t("Legal Entities")}</Label>
              <Select
                isMulti
                placeholder={t("Select") + "..."}
                options={legalEntityOptions}
                // value={selectedLegalEntities}
                onChange={setSelectedLegalEntities}
                isLoading={legalEntitiesFetching}
                isDisabled={legalEntitiesFetching}
              />
            </FormGroup>
          </Col>

          <Col sm="12" className="mb-2">
            <FormGroup check>
              <Input
                type="checkbox"
                checked={copySubcontractors}
                onChange={(e) => setCopySubcontractors(e.target.checked)}
              />
              <Label check>{t("Copy subcontractors")}</Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter>
        <Button onClick={hideModal} color="primary" type="button" outline>
          {t("Cancel")}
        </Button>

        <Button
          disabled={actionInProgress}
          color="primary"
          type="button"
          size="md"
          onClick={handleSubmit}
        >
          {actionInProgress ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="ri-file-copy-line align-middle me-2"></i>
          )}
          {t("Clone")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStatetoProps = (state) => {
  const { token, user } = state.Login;
  const { Organization } = state;

  return {
    token,
    user,
    Organization,
  };
};

export default withNamespaces()(
  connect(mapStatetoProps)(withRouter(CloneModal))
);